import React from 'react';
import { useTranslation } from 'react-i18next';
import type { LearningSkeletonKind } from './learningSkeletonKind';
import { NS } from './i18n';
import './LearningSkeleton.css';

/*
 * Local counts, not the catalogue's: data/catalog.ts imports the generated JSON, and this file
 * is in the initial bundle.
 */
const MENU_ROWS = [0, 1];
const MENU_STAGES = [0, 1, 2, 3, 4, 5, 6, 7];
const TRACK = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const PATH_ITEMS = [0, 1, 2];
const LATEST_ITEMS = [0, 1, 2, 3];
const REFERENCES = [0, 1, 2];
const TASKS = [0, 1, 2, 3, 4];
const HOW_LINES = [0, 1, 2, 3];
const FILTERS = [0, 1, 2];
const RELEASES = [0, 1, 2];
const RELEASE_ITEMS = [0, 1, 2];
const MCP_STEPS = [0, 1, 2, 3];
const MCP_TOOLS = [0, 1, 2, 3, 4];

const Bone: React.FC<{ className: string }> = ({ className }) => (
  <span className={`learning-skeleton__bone ${className}`} />
);

/** One release of Novedades: version and date in the narrow column, the notes beside them. */
function ReleaseList(): React.ReactElement {
  return (
    <div className="learning-skeleton__releases">
      {RELEASES.map((release) => (
        <div key={release} className="learning-skeleton__release">
          <div className="learning-skeleton__release-aside">
            <Bone className="learning-skeleton__version" />
            <Bone className="learning-skeleton__date" />
          </div>
          <div className="learning-skeleton__stack">
            <Bone className="learning-skeleton__line learning-skeleton__line--long" />
            <Bone className="learning-skeleton__subheading" />
            {RELEASE_ITEMS.map((item) => (
              <Bone
                key={item}
                className="learning-skeleton__line learning-skeleton__line--medium"
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** The latest release, as the home's news panel shows it: version, date, four items. */
function LatestRelease(): React.ReactElement {
  return (
    <div className="learning-skeleton__stack">
      <div className="learning-skeleton__row">
        <Bone className="learning-skeleton__version" />
        <Bone className="learning-skeleton__date" />
      </div>
      <Bone className="learning-skeleton__line learning-skeleton__line--long" />
      {LATEST_ITEMS.map((item) => (
        <div key={item} className="learning-skeleton__row">
          <Bone className="learning-skeleton__label" />
          <Bone className="learning-skeleton__line learning-skeleton__line--medium" />
        </div>
      ))}
    </div>
  );
}

/** A page's heading inside the frame: title and lead. */
const Heading: React.FC = () => (
  <div className="learning-skeleton__stack">
    <Bone className="learning-skeleton__heading" />
    <Bone className="learning-skeleton__line learning-skeleton__line--long" />
  </div>
);

// ── Panels: what each page draws beside the menu ─────────────────────────

/** The route's progress and start, the path card, then news and the technical reference. */
function HomePanel(): React.ReactElement {
  return (
    <div className="learning-skeleton__panel learning-skeleton__panel--home">
      <div className="learning-skeleton__stack">
        <Bone className="learning-skeleton__line learning-skeleton__line--lead" />
        <Bone className="learning-skeleton__line learning-skeleton__line--medium" />
        <div className="learning-skeleton__split">
          <Bone className="learning-skeleton__label" />
          <Bone className="learning-skeleton__count" />
        </div>
        <div className="learning-skeleton__track">
          {TRACK.map((segment) => (
            <Bone key={segment} className="learning-skeleton__segment" />
          ))}
        </div>
        <div className="learning-skeleton__row">
          <Bone className="learning-skeleton__button learning-skeleton__button--wide" />
          <Bone className="learning-skeleton__button" />
        </div>
      </div>
      <div className="learning-skeleton__box">
        <Bone className="learning-skeleton__subheading" />
        <Bone className="learning-skeleton__line learning-skeleton__line--long" />
        {PATH_ITEMS.map((item) => (
          <Bone key={item} className="learning-skeleton__line learning-skeleton__line--medium" />
        ))}
        <Bone className="learning-skeleton__button" />
      </div>
      <div className="learning-skeleton__columns">
        <div className="learning-skeleton__stack">
          <div className="learning-skeleton__split learning-skeleton__split--rule">
            <Bone className="learning-skeleton__subheading" />
            <Bone className="learning-skeleton__count" />
          </div>
          <LatestRelease />
        </div>
        <div className="learning-skeleton__stack">
          <div className="learning-skeleton__split learning-skeleton__split--rule">
            <Bone className="learning-skeleton__subheading" />
          </div>
          {REFERENCES.map((reference) => (
            <div key={reference} className="learning-skeleton__row">
              <Bone className="learning-skeleton__icon" />
              <Bone className="learning-skeleton__line learning-skeleton__line--medium" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** A stage: its heading, the numbered tasks and the pager, the "how" card beside them. */
function StagePanel(): React.ReactElement {
  return (
    <div className="learning-skeleton__panel learning-skeleton__stage">
      <div className="learning-skeleton__stack">
        <Bone className="learning-skeleton__eyebrow" />
        <Heading />
        <Bone className="learning-skeleton__count" />
        <div className="learning-skeleton__tasks">
          {TASKS.map((task) => (
            <div key={task} className="learning-skeleton__task">
              <Bone className="learning-skeleton__num" />
              <div className="learning-skeleton__stack">
                <Bone className="learning-skeleton__line learning-skeleton__line--medium" />
                <Bone className="learning-skeleton__line learning-skeleton__line--long" />
              </div>
            </div>
          ))}
        </div>
        <div className="learning-skeleton__split">
          <Bone className="learning-skeleton__button" />
          <Bone className="learning-skeleton__button" />
        </div>
      </div>
      <div className="learning-skeleton__box learning-skeleton__how">
        <Bone className="learning-skeleton__eyebrow" />
        <Bone className="learning-skeleton__subheading" />
        {HOW_LINES.map((line) => (
          <Bone key={line} className="learning-skeleton__line learning-skeleton__line--long" />
        ))}
        <Bone className="learning-skeleton__button learning-skeleton__button--end" />
      </div>
    </div>
  );
}

/** Novedades: heading and the section filter, then the releases. */
function NewsPanel(): React.ReactElement {
  return (
    <div className="learning-skeleton__panel">
      <div className="learning-skeleton__split learning-skeleton__split--wrap">
        <Heading />
        <div className="learning-skeleton__segmented">
          {FILTERS.map((filter) => (
            <Bone key={filter} className="learning-skeleton__segment-option" />
          ))}
        </div>
      </div>
      <ReleaseList />
    </div>
  );
}

/** The MCP guide: four numbered steps with their command, then the tools table. */
function McpPanel(): React.ReactElement {
  return (
    <div className="learning-skeleton__panel learning-skeleton__panel--mcp">
      <Heading />
      {MCP_STEPS.map((step) => (
        <div key={step} className="learning-skeleton__task">
          <Bone className="learning-skeleton__num" />
          <div className="learning-skeleton__stack">
            <Bone className="learning-skeleton__subheading" />
            <Bone className="learning-skeleton__line learning-skeleton__line--long" />
            <Bone className="learning-skeleton__code" />
          </div>
        </div>
      ))}
      <div className="learning-skeleton__table">
        {MCP_TOOLS.map((tool) => (
          <div key={tool} className="learning-skeleton__table-row">
            <Bone className="learning-skeleton__line learning-skeleton__line--medium" />
            <Bone className="learning-skeleton__line learning-skeleton__line--long" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Exhaustive: a new kind fails to type-check until it has a panel. */
const PANELS: Record<LearningSkeletonKind, React.FC> = {
  home: HomePanel,
  stage: StagePanel,
  news: NewsPanel,
  mcp: McpPanel,
};

const Panel: React.FC<{ kind: LearningSkeletonKind }> = ({ kind }) => {
  const Bones = PANELS[kind];
  return <Bones />;
};

/** The frame LearningLayout draws: breadcrumb, the page card and its title, menu beside page. */
const Frame: React.FC<{ kind: LearningSkeletonKind }> = ({ kind }) => (
  <>
    <Bone className="learning-skeleton__crumb" />
    <div className="learning-skeleton__card">
      <Bone className="learning-skeleton__title" />
      <div className="learning-skeleton__frame">
        <div className="learning-skeleton__nav">
          <div className="learning-skeleton__menu">
            {MENU_ROWS.map((row) => (
              <div key={row} className="learning-skeleton__row">
                <Bone className="learning-skeleton__dot" />
                <Bone className="learning-skeleton__line learning-skeleton__line--medium" />
              </div>
            ))}
            <Bone className="learning-skeleton__label" />
            {MENU_STAGES.map((row) => (
              <div key={row} className="learning-skeleton__row">
                <Bone className="learning-skeleton__tile" />
                <Bone className="learning-skeleton__line learning-skeleton__line--long" />
              </div>
            ))}
          </div>
          <Bone className="learning-skeleton__picker" />
        </div>
        <Panel kind={kind} />
      </div>
    </div>
  </>
);

export interface LearningSkeletonProps {
  kind: LearningSkeletonKind;
}

/**
 * Loading shape of a Silia Learning screen while LearningLayout's chunk loads: breadcrumb, the
 * page card, the menu and that screen's panel. The route's Suspense fallback and RouteSkeleton
 * both draw this one, so a cold load keeps one shape. No `aria-busy`: on a live region it
 * holds the announcement back.
 */
export const LearningSkeleton: React.FC<LearningSkeletonProps> = ({ kind }) => {
  const { t } = useTranslation(NS);
  return (
    <div
      className="learning-skeleton learning-skeleton--page"
      role="status"
      data-testid="learning-skeleton"
      data-kind={kind}
    >
      <span className="learning-skeleton__sr-only">{t(kind)}</span>
      <div className="learning-skeleton__body" aria-hidden="true">
        <Frame kind={kind} />
      </div>
    </div>
  );
};

/**
 * One page's shape inside the drawn frame, while that page's chunk loads: LearningLayout's
 * Suspense around its <Outlet/>, so the menu stays put. It is the only live region there.
 */
export const LearningPanelSkeleton: React.FC<LearningSkeletonProps> = ({ kind }) => {
  const { t } = useTranslation(NS);
  return (
    <div
      className="learning-skeleton"
      role="status"
      data-testid="learning-panel-skeleton"
      data-kind={kind}
    >
      <span className="learning-skeleton__sr-only">{t(kind)}</span>
      <div className="learning-skeleton__body" aria-hidden="true">
        <Panel kind={kind} />
      </div>
    </div>
  );
};

export interface LearningReleasesSkeletonProps {
  /** Novedades' list of releases, or the latest one on the home's news panel. */
  variant: 'list' | 'latest';
}

/** The release notes while they load, inside a page that is already drawn. */
export const LearningReleasesSkeleton: React.FC<LearningReleasesSkeletonProps> = ({ variant }) => {
  const { t } = useTranslation(NS);
  return (
    <div
      className="learning-skeleton"
      role="status"
      data-testid="learning-releases-skeleton"
      data-variant={variant}
    >
      <span className="learning-skeleton__sr-only">{t('releases')}</span>
      <div className="learning-skeleton__body" aria-hidden="true">
        {variant === 'list' ? <ReleaseList /> : <LatestRelease />}
      </div>
    </div>
  );
};
